import { Box, Button, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import type { HnValidateAddressInput } from 'CDK_API';
import { sentryMask } from 'sentryReplayPrivacy';

export interface AddressCorrectionDialogProps {
  open: boolean;
  original: HnValidateAddressInput;
  corrected: HnValidateAddressInput;
  onResolve: (decision: 'accept' | 'decline') => void;
}

function formatAddress(address: HnValidateAddressInput): string[] {
  const lines: string[] = [address.streetAddress];
  if (address.extendedAddress) lines.push(address.extendedAddress);
  lines.push(`${address.locality}, ${address.region} ${address.postalCode}`);
  return lines;
}

function AddressCorrectionDialog({ open, original, corrected, onResolve }: AddressCorrectionDialogProps) {
  const { t } = useTranslation();
  return (
    // A portal, so no page container reaches it: the dialog root masks the address lines, and the
    // title, column headings (`subtitle2` is an `h6`) and buttons stay visible as chrome.
    <ResponsiveDialog
      open={open}
      onClose={() => onResolve('decline')}
      aria-labelledby="address-correction-dialog-title"
      {...sentryMask}
    >
      <DialogTitle id="address-correction-dialog-title">
        <Typography variant="h2">{t('addressCorrectionTitle')}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Box flex={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('addressCorrectionYouEntered')}
            </Typography>
            {formatAddress(original).map((line, i) => (
              <Typography key={`original-${i}`} variant="body2">
                {line}
              </Typography>
            ))}
          </Box>
          <Box flex={1}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {t('addressCorrectionSuggested')}
            </Typography>
            {formatAddress(corrected).map((line, i) => (
              <Typography key={`corrected-${i}`} variant="body2">
                {line}
              </Typography>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <ResponsiveDialogActions>
        <Button variant="outlined" onClick={() => onResolve('decline')}>
          {t('addressCorrectionKeep')}
        </Button>
        <Button variant="contained" onClick={() => onResolve('accept')}>
          {t('addressCorrectionAccept')}
        </Button>
      </ResponsiveDialogActions>
    </ResponsiveDialog>
  );
}

export default AddressCorrectionDialog;
